import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Linkedin, Github, Youtube } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const socialIcons = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  github: Github,
  youtube: Youtube
};

const Footer = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [socials, setSocials] = useState({});

  useEffect(() => {
    api.getSiteData().then(response => {
      setContent(response.data[language].footer);
      setSocials(response.data.socials);
    }).catch(err => console.error('Error loading footer data:', err));
  }, [language]);

  if (!content) return null;

  const activeSocials = Object.entries(socials).filter(([_, url]) => url);

  return (
    <footer className="relative bg-[#0a0a0b] border-t border-cyan-500/10 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col items-center">
          {/* Logo/Name */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h3 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Loranrix
            </h3>
          </motion.div>

          {/* Social links */}
          {activeSocials.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-4 mb-8"
            >
              {activeSocials.map(([platform, url]) => {
                const Icon = socialIcons[platform];
                return (
                  <motion.a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.2, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300"
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </motion.div>
          )}

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-gray-500 text-sm mb-4 font-mono text-center text-balance max-w-prose px-4 break-words"
          >
            {content.tagline}
          </motion.p>

          {/* Copyright */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-gray-600 text-sm"
          >
            {content.copyright}
          </motion.p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
